import React, { useCallback, useMemo, useState } from "react";
import { UserAdminEmployees_store$key } from "./__generated__/UserAdminEmployees_store.graphql";
import { graphql, useFragment } from "react-relay";
import {
  AHSeparator,
  Box,
  EmployeeAvatar,
  ListItem,
  useLayoutContext,
} from "arkh";
import { SearchInput } from "../Products/SearchInput";
import { FlashList } from "@shopify/flash-list";
import { keyExtractorId } from "@/src/common/keyExtractors";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { FlatList, Platform } from "react-native";

import { StoreUserRoleAndEmailTag } from "./StoreUserRoleAndEmailTag";
import { ActionFooter } from "@/src/components/ActionFooter";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";

import { KeyboardAwareFlashListScrollComponent } from "@/src/components/mobile/KeyboardAwareFlashListScrollComponent";

export function UserAdminEmployees(props: {
  store: UserAdminEmployees_store$key;
  openEmployee(id: string): void;
  createEmployee(): void;
}) {
  const [query, setQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const safeAreaInsets = useSafeAreaInsets();

  const store = useFragment(
    graphql`
      fragment UserAdminEmployees_store on Store {
        id
        hasEmployeeProvider
        employees(orderBy: NAME) {
          edges {
            node {
              id
              name
              ...Avatar_employee
              storeUser {
                id
                ...StoreUserRoleAndEmailTag_storeUser
                user {
                  id
                  email
                }
                role
              }
            }
          }
        }
      }
    `,
    props.store
  );

  const listData = useMemo(() => {
    const lowerCaseQuery = query.toLocaleLowerCase();
    return (
      store.employees?.edges
        .map((e) => e.node)
        .filter((e) => {
          if (e.name?.toLocaleLowerCase().includes(lowerCaseQuery)) {
            return true;
          } else {
            return false;
          }
        }) ?? []
    );
  }, [query, store.employees?.edges]);

  const renderItem = useCallback(
    ({ item }: { item: (typeof listData)[number] }) => {
      return (
        <ListItem
          left={<EmployeeAvatar employee={item} size="large" />}
          right={<ListItem.NavArrow />}
          onPress={() => {
            props.openEmployee(item.id);
          }}
          footer={
            item.storeUser ? (
              <StoreUserRoleAndEmailTag storeUser={item.storeUser} />
            ) : null
          }
        >
          <ListItem.Title>{item.name}</ListItem.Title>
        </ListItem>
      );
    },
    [props]
  );

  const layout = useLayoutContext();
  const intl_tokens = useIntlTokens();

  return (
    <>
      <Box color="surface-primary">
        <SearchInput
          query={query}
          onChange={(e) => {
            setQuery(e.nativeEvent.text);
          }}
          onFocus={() => setIsSearching(true)}
          onBlur={() => {
            setQuery("");
            setIsSearching(false);
          }}
          isSearching={isSearching}
          returnKeyType="done"
          autoCapitalize="none"
          showCancel={true}
          hideSeparator
        />
      </Box>
      <AHSeparator />
      {Platform.OS === "web" ? (
        <FlatList
          data={listData}
          renderItem={renderItem}
          ItemSeparatorComponent={AHSeparator}
          keyExtractor={keyExtractorId}
          keyboardShouldPersistTaps="handled"
          scrollIndicatorInsets={{
            bottom: layout.space.medium + safeAreaInsets.bottom,
          }}
          contentContainerStyle={{
            paddingBottom: layout.space.medium + safeAreaInsets.bottom,
          }}
        />
      ) : (
        <FlashList
          data={listData}
          renderItem={renderItem}
          keyExtractor={keyExtractorId}
          ItemSeparatorComponent={AHSeparator}
          keyboardShouldPersistTaps="handled"
          scrollIndicatorInsets={{
            bottom: layout.space.medium + safeAreaInsets.bottom,
          }}
          renderScrollComponent={KeyboardAwareFlashListScrollComponent}
          contentContainerStyle={{
            paddingBottom: layout.space.medium + safeAreaInsets.bottom,
          }}
        />
      )}
      {!store.hasEmployeeProvider ? (
        <ActionFooter
          primary={
            <ActionFooter.Primary
              title={intl_tokens.AddEmployee}
              icon="add"
              onPress={() => {
                props.createEmployee();
              }}
            />
          }
        />
      ) : null}
    </>
  );
}
